import { Button, Dialog, IconButton, Text, TextField, Tooltip, Flex } from '@radix-ui/themes';
import { Users } from 'lucide-react';
import { Form } from 'radix-ui';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SubmitData = {
  name: string;
};

const CreateAccessGroup = ({
  onSubmit,
  loading,
  disable = false,
}: {
  onSubmit: (name: string) => Promise<void>;
  loading: boolean;
  disable: boolean;
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleSubmit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();

    const form = formEvent.currentTarget;
    const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;
    const name = formData.name.trim();

    onSubmit(name).then(() => {
      setDialogOpen(false);
    });
  };

  return (
    <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
      <Tooltip
        content={
          <Text size="3">
            {disable
              ? t('settings.accessGroupDialogs.createAccessGroup.noPermissionTooltip')
              : t('settings.accessGroupDialogs.createAccessGroup.createGroupTooltip')}
          </Text>
        }
      >
        <Dialog.Trigger>
          <IconButton variant="ghost" color="gray" highContrast className="m-0" disabled={disable}>
            <Users />
          </IconButton>
        </Dialog.Trigger>
      </Tooltip>
      <Dialog.Content style={{ maxWidth: 450 }} aria-describedby={undefined}>
        <Dialog.Title>{t('settings.accessGroupDialogs.createAccessGroup.dialogTitle')}</Dialog.Title>

        <Form.Root onSubmit={handleSubmit}>
          <Form.Field name="name" className="mb-4">
            <Text size="1" color="red" className="flex">
              <Form.Message match="valueMissing">
                {t('settings.accessGroupDialogs.createAccessGroup.nameRequired')}
              </Form.Message>
            </Text>
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('settings.accessGroupDialogs.createAccessGroup.namePlaceholder')}
                required
                className="w-full"
                size="3"
              />
            </Form.Control>
          </Form.Field>

          <Flex gap="3" mt="4" justify="between">
            <Dialog.Close>
              <Button type="reset" color="gray" size="3" variant="soft">
                {t('settings.accessGroupDialogs.createAccessGroup.cancelButton')}
              </Button>
            </Dialog.Close>
            <Form.Submit asChild>
              <Button size="3" loading={loading}>
                {t('settings.accessGroupDialogs.createAccessGroup.createButton')}
              </Button>
            </Form.Submit>
          </Flex>
        </Form.Root>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default CreateAccessGroup;
